import React, { useCallback, useEffect, useRef, useState } from "react"
import AdminLayout from "../admin/adminLayout"
import { Input, Form, Button } from "antd"
import "./style.scss"
import { useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { adminLoginAction } from "../../store/sagas/adminAuthSaga"
import { RootState } from "store"
import { AdminLoginParams } from "types/auth"
import { setAdminLoginError, setIsAdminLoggedIn } from "store/duck/adminAuthSlice"
import axios from "config/axios"
import constant from "constant/action"

const ERROR_MESSAGE_HIDE_DELAY_MS = 5000
const RANKING_CSV_DOWNLOAD_ONLY_ROUTE = "__ranking_csv_download_only__"
const RANKING_CSV_RELOAD_GUARD_KEY = "__ranking_csv_reload_guard_at__"
const RANKING_CSV_RELOAD_GUARD_TTL_MS = 30 * 1000
const BLOB_URL_REVOKE_DELAY_MS = 5000
const LOGIN_ERROR_MESSAGE = "入力情報が正しくありません。再入力してください。"

const padTimestampPart = (value: number): string => value.toString().padStart(2, "0")

const formatLocalTimestamp = (date: Date): string =>
  `${date.getFullYear()}${padTimestampPart(date.getMonth() + 1)}${padTimestampPart(date.getDate())}_${padTimestampPart(date.getHours())}${padTimestampPart(date.getMinutes())}${padTimestampPart(date.getSeconds())}`

const markRankingCsvReloadGuard = () => {
  try {
    sessionStorage.setItem(RANKING_CSV_RELOAD_GUARD_KEY, Date.now().toString())
  } catch (_error) {
    // ignore storage errors
  }
}

const consumeRankingCsvReloadGuard = (): boolean => {
  try {
    const rawValue = sessionStorage.getItem(RANKING_CSV_RELOAD_GUARD_KEY)
    sessionStorage.removeItem(RANKING_CSV_RELOAD_GUARD_KEY)
    if (!rawValue) {
      return false
    }

    const markedAt = Number(rawValue)
    if (!Number.isFinite(markedAt)) {
      return false
    }
    return Date.now() - markedAt <= RANKING_CSV_RELOAD_GUARD_TTL_MS
  } catch (_error) {
    return false
  }
}
const layout = {
  labelCol: {
    span: 8,
  },
  wrapperCol: {
    span: 16,
  },
}

const Login = () => {
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { isAdminLoggedIn, adminLoginError } = useSelector(
    (state: RootState) => state.adminAuth
  )
  const [targetRoute, setTargetRoute] = useState<string | null>(null)
  const [pendingRankingCsvExport, setPendingRankingCsvExport] = useState<boolean>(false)
  const [requiredError, setRequiredError] = useState<boolean>(false)
  const [loginError, setLoginError] = useState<boolean>(false)
  const [csvExportError, setCsvExportError] = useState<boolean>(false)
  const [isRankingCsvDownloading, setIsRankingCsvDownloading] = useState<boolean>(false)
  const isMountedRef = useRef<boolean>(true)
  const suppressInitialAutoNavigateRef = useRef<boolean>(false)
  const clearAdminSession = useCallback(() => {
    dispatch(setIsAdminLoggedIn(false))
    localStorage.removeItem("token")
    if (localStorage.getItem("secondary_token")) {
      localStorage.removeItem("secondary_token")
    }
  }, [dispatch])

  const onSubmitLogin = async (values: any) => {
    setCsvExportError(false)
    if (!values.account_id || !values.factory_code || !values.password) {
      setRequiredError(true)
      return
    }
    const data: AdminLoginParams = {
      account_id: values?.account_id,
      factory_code: values?.factory_code,
      password: values?.password,
    }
    // 再認証時は旧セッションを先に明示クリアして状態整合を保つ
    clearAdminSession()
    dispatch(adminLoginAction(data))
  }

  const downloadRankingCsv = useCallback(async (): Promise<boolean> => {
    if (isRankingCsvDownloading) {
      return false
    }
    try {
      if (isMountedRef.current) {
        setIsRankingCsvDownloading(true)
        setCsvExportError(false)
      }
      const response = await axios.get(constant.RANKING_CSV_EXPORT, {
        responseType: "blob",
      })

      const url = window.URL.createObjectURL(response.data)
      const link = document.createElement("a")
      link.href = url

      const now = new Date()
      const timestamp = formatLocalTimestamp(now)
      link.setAttribute("download", `ranking_ct_diff_monthly_${timestamp}.csv`)

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.setTimeout(() => {
        window.URL.revokeObjectURL(url)
      }, BLOB_URL_REVOKE_DELAY_MS)
      markRankingCsvReloadGuard()
      clearAdminSession()
      return true
    } catch (error) {
      console.error("Ranking CSV export failed:", error)
      clearAdminSession()
      if (isMountedRef.current) {
        setCsvExportError(true)
      }
      return false
    } finally {
      if (isMountedRef.current) {
        setIsRankingCsvDownloading(false)
      }
    }
  }, [clearAdminSession, isRankingCsvDownloading])

  useEffect(() => {
    isMountedRef.current = true
    suppressInitialAutoNavigateRef.current = consumeRankingCsvReloadGuard()
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (adminLoginError) {
      form.resetFields()
      setLoginError(true)
      setPendingRankingCsvExport(false)
      setTargetRoute(null)
      setCsvExportError(false)
      dispatch(setAdminLoginError(false))
    }
  }, [adminLoginError, dispatch, form])

  useEffect(() => {
    if (!isAdminLoggedIn) {
      return
    }

    if (pendingRankingCsvExport) {
      setPendingRankingCsvExport(false)
      void downloadRankingCsv()
      return
    }

    if (targetRoute === RANKING_CSV_DOWNLOAD_ONLY_ROUTE) {
      return
    }

    if (targetRoute) {
      navigate(targetRoute)
      return
    }

    if (suppressInitialAutoNavigateRef.current) {
      suppressInitialAutoNavigateRef.current = false
      return
    }
    navigate("/admin/device-settings")
  }, [isAdminLoggedIn, targetRoute, pendingRankingCsvExport, navigate, downloadRankingCsv])

  useEffect(() => {
    let requiredErrorTimeout: NodeJS.Timeout
    if (requiredError) {
      requiredErrorTimeout = setTimeout(() => {
        setRequiredError(false)
      }, ERROR_MESSAGE_HIDE_DELAY_MS)
    }
    return () => clearTimeout(requiredErrorTimeout)
  }, [requiredError])

  useEffect(() => {
    let loginErrorTimeout: NodeJS.Timeout
    if (loginError) {
      loginErrorTimeout = setTimeout(() => {
        setLoginError(false)
      }, ERROR_MESSAGE_HIDE_DELAY_MS)
    }
    return () => clearTimeout(loginErrorTimeout)
  }, [loginError])

  useEffect(() => {
    let csvExportErrorTimeout: NodeJS.Timeout
    if (csvExportError) {
      csvExportErrorTimeout = setTimeout(() => {
        setCsvExportError(false)
      }, ERROR_MESSAGE_HIDE_DELAY_MS)
    }
    return () => clearTimeout(csvExportErrorTimeout)
  }, [csvExportError])

  return (
    <>
      <AdminLayout>
        <div className="login-form">
          <Form
            {...layout}
            form={form}
            name="nest-messages"
            onFinish={(values) => {
              onSubmitLogin(values)
            }}
          >
            <p className="title-login">工場ID</p>
            <Form.Item name="factory_code">
              <Input className="input-login" />
            </Form.Item>
            <p className="title-login">管理者ID</p>
            <Form.Item name="account_id">
              <Input className="input-login" />
            </Form.Item>
            <p className="title-login">パスワード</p>
            <Form.Item name="password">
              <Input type="password" className="input-login" />
            </Form.Item>
            <div className="flex justify-center min-w-[590px] gap-x-[30px] mt-6">
              <div className="w-[200px]">
                <Button
                  className="button-login bt-settings font-noto-sans"
                  onClick={() => {
                    clearAdminSession()
                    setPendingRankingCsvExport(false)
                    setTargetRoute("/admin/device-settings")
                    form.submit()
                  }}
                >
                  設定
                </Button>
              </div>
              <div className="w-[200px]">
                <Button
                  className="button-login bt-ranking-history font-noto-sans"
                  disabled={isRankingCsvDownloading}
                  onClick={() => {
                    clearAdminSession()
                    setTargetRoute(RANKING_CSV_DOWNLOAD_ONLY_ROUTE)
                    setPendingRankingCsvExport(true)
                    form.submit()
                  }}
                >
                  ランキング履歴
                </Button>
              </div>
            </div>
            <div className="flex justify-center min-w-[590px] gap-x-[30px] mt-6">
              <div className="w-[200px]">
                <Button
                  className="button-login font-noto-sans"
                  onClick={() => {
                    clearAdminSession()
                    setPendingRankingCsvExport(false)
                    setTargetRoute("/admin/skill-check")
                    form.submit()
                  }}
                >
                  スキル確認
                </Button>
              </div>
              <div className="w-[200px]">
                <Button
                  className="button-login bt-skill-map font-noto-sans"
                  onClick={() => {
                    clearAdminSession()
                    setPendingRankingCsvExport(false)
                    setTargetRoute("/admin/skill-map")
                    form.submit()
                  }}
                >
                  スキルマップ
                </Button>
              </div>
            </div>
            <div className="flex justify-center mt-6">
              <div className="w-[200px]">
                {/* <Button
                  className="button-login bt-reset font-noto-sans"
                  onClick={() => {
                    clearAdminSession()
                    setPendingRankingCsvExport(false)
                    setTargetRoute("/admin/reset")
                    form.submit()
                  }}
                >
                  データリセット
                </Button> */}
              </div>
            </div>
          </Form>
          <div className="font-noto-sans h-12 pt-6 font-bold text-center text-[16px] text-red-600">
            {loginError
              ? LOGIN_ERROR_MESSAGE
              : requiredError
              ? "このフィールドは入力必須です"
              : csvExportError
              ? "ランキング履歴のダウンロードに失敗しました。再度お試しください。"
              : ""}
          </div>
        </div>
      </AdminLayout>
    </>
  )
}

export default Login
